/**
 * React Query hooks for admin dashboard data
 *
 * Provides hooks for fetching dashboard statistics with auto-refresh capabilities.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { dashboardApi } from '../../infrastructure/api/DashboardApi';
import type {
  AdminDashboardDto,
  RateLimitSection
} from '../../domain/entities/AdminDashboard';

/**
 * Query keys for dashboard data
 * Used for cache invalidation and refetching
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  rateLimit: () => [...dashboardKeys.all, 'rateLimit'] as const
};

/**
 * Hook to fetch complete dashboard statistics
 *
 * @param refetchInterval - Optional interval in milliseconds for auto-refresh (default: 30000ms / 30 seconds)
 * @returns Query result with dashboard data
 *
 * @example
 * const { data, isLoading, error } = useDashboardStats();
 * // With custom refresh interval
 * const { data } = useDashboardStats(60000); // Refresh every minute
 * // Disable auto-refresh
 * const { data } = useDashboardStats(false);
 */
export function useDashboardStats(
  refetchInterval: number | false = 30000
): UseQueryResult<AdminDashboardDto, Error> {
  return useQuery<AdminDashboardDto, Error>({
    queryKey: dashboardKeys.stats(),
    queryFn: () => dashboardApi.getDashboardStats(),
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchInterval: refetchInterval, // Auto-refresh interval
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Refetch when component mounts
    retry: 2, // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff
  });
}

/**
 * Hook to fetch rate limit status only
 *
 * This endpoint may return null if no Reddit API calls have been made yet.
 * The RateLimitMonitor (backend) only has data after the first Reddit API call.
 *
 * @param refetchInterval - Optional interval in milliseconds for auto-refresh (default: 30000ms / 30 seconds)
 * @returns Query result with rate limit data (or null)
 *
 * @example
 * const { data: rateLimit } = useRateLimit();
 * if (rateLimit === null) {
 *   return <Text>No rate limit data available yet</Text>;
 * }
 */
export function useRateLimit(
  refetchInterval: number | false = 30000
): UseQueryResult<RateLimitSection | null, Error> {
  return useQuery<RateLimitSection | null, Error>({
    queryKey: dashboardKeys.rateLimit(),
    queryFn: () => dashboardApi.getRateLimit(),
    staleTime: 30000,
    refetchInterval: refetchInterval,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 1, // Less retries for optional data
    retryDelay: 1000
  });
}
